import {z} from 'zod';
import {GoogleSyncResult} from '../entryCalendars/schema';
import {
  MANAGEMENT_OVERALL_STATUS_VALUES,
  PROGRESS_RESULT_VALUES,
} from './constants';

// 一覧API用: GET /3.0/service/users/me/entry-managements
export const LatestStep = z.object({
  id: z.number(),
  userEntryManagementID: z.number(), // 選考管理IDへの参照（大文字ID）
  stepName: z.string(),
  stepOrder: z.number(),
  sourceType: z.string(),
  entryTitle: z.string().nullable(),
  entrySourceUrl: z.string().nullable(),
  entrySourceType: z.string().nullable(),
  entryDate: z.string().nullable(),
  result: z.string().nullable(),
  notes: z.string().nullable(),
  stepDate: z.string().nullable().optional(),
  deadline: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  place: z.string().nullable(),
  created: z.string(),
  modified: z.string(),
});

/**
 * 選考管理の全体ステータススキーマ
 */
const ManagementOverAllStatus = z.enum([
  MANAGEMENT_OVERALL_STATUS_VALUES.active,
  MANAGEMENT_OVERALL_STATUS_VALUES.completed,
  MANAGEMENT_OVERALL_STATUS_VALUES.archived,
  MANAGEMENT_OVERALL_STATUS_VALUES.rejected,
]);

export type ManagementOverAllStatusType = z.infer<
  typeof ManagementOverAllStatus
>;

/**
 * 選考予定の結果スキーマ
 * nullable: 結果が未入力の場合はnull
 */
const ProgressResult = z
  .enum([
    PROGRESS_RESULT_VALUES.pending,
    PROGRESS_RESULT_VALUES.accepted,
    PROGRESS_RESULT_VALUES.completed,
    PROGRESS_RESULT_VALUES.rejected,
    PROGRESS_RESULT_VALUES.declined,
  ])
  .nullable();

export type ProgressResultType = z.infer<typeof ProgressResult>;

// 一覧API用: 選考管理サマリー
export const EntryManagementSummary = z.object({
  id: z.number(),
  companyName: z.string(),
  companyID: z.number().nullable(),
  overallStatus: ManagementOverAllStatus,
  hasCompletedEntry: z.boolean().optional(),
  created: z.string(),
  modified: z.string(),
  latestSteps: z.array(LatestStep).optional(),
});

export const GetEntryManagementsOutput = z.object({
  entries: z.array(EntryManagementSummary),
  totalCount: z.number(),
  page: z.number(),
  limit: z.number(),
});

// GET /3.0/service/users/me/entry-managements/{id} - 選考管理詳細API
export const EntryManagementDetailStep = z.object({
  id: z.number(),
  userEntryManagementID: z.number(), // 大文字ID
  stepName: z.string(),
  stepOrder: z.number(),
  sourceType: z.string(),
  entryTitle: z.string().nullable(),
  entrySourceUrl: z.string().nullable(),
  entrySourceType: z.string().nullable(),
  entryDate: z.string().nullable(),
  result: ProgressResult,
  notes: z.string().nullable(),
  stepDate: z.string().nullable().optional(),
  deadline: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  place: z.string().nullable(),
  created: z.string(),
  modified: z.string(),
});

export const EntryManagementDetail = z.object({
  id: z.number(),
  companyName: z.string(),
  companyID: z.number(),
  overallStatus: ManagementOverAllStatus,
  hasCompletedEntry: z.boolean().optional(),
  logoUrls: z
    .object({
      '48x36': z.string(),
      '140x105': z.string(),
      '200x150': z.string(),
      '400x300': z.string(),
      default: z.string(),
    })
    .optional(),
  created: z.string(),
  modified: z.string(),
  steps: z.array(EntryManagementDetailStep).optional(),
});

// POST /3.0/service/users/me/entry-managements - 選考管理作成API
// 初期選考予定定義
export const CreateEntryManagementStepInput = z.object({
  stepName: z.string(),
  stepOrder: z.number(),
  entryTitle: z.string().optional(),
  entrySourceUrl: z.string().optional(),
  entrySourceType: z.string().optional(),
  entryDate: z.string().optional(), // YYYY-MM-DD形式
  scheduleID: z.number().optional(),
  result: z.string().optional(),
  notes: z.string().optional(),
  deadline: z.string().optional(), // YYYY-MM-DD形式
  startDate: z.string().optional(), // YYYY-MM-DD形式
  endDate: z.string().optional(), // YYYY-MM-DD形式
  place: z.string().optional(),
});

// レスポンスに含まれる作成された選考予定
export const CreatedStep = z.object({
  id: z.number(),
  userEntryManagementID: z.number(),
  stepName: z.string(),
  stepOrder: z.number(),
  created: z.string(),
  modified: z.string(),
});

export const CreateEntryManagementOutput = z.object({
  id: z.number(),
  companyID: z.number(),
  companyName: z.string(),
  overallStatus: z.string(),
  steps: z.array(CreatedStep),
});

// PUT /3.0/service/users/me/entry-managements/{id} - 選考管理更新API
// 選考予定追加・更新・削除用（増分更新方式）
// idあり: 既存選考予定を更新、idなし: 新規作成、リクエストに含まれないid: 削除
export const UpdateEntryManagementStepInput = z.object({
  id: z.number().optional(), // 既存選考予定の場合は必須、新規は不要
  stepName: z.string(),
  stepOrder: z.number(),
  sourceType: z.string().nullable().optional(), // バックエンドで実装済み
  entryTitle: z.string().nullable().optional(),
  entrySourceUrl: z.string().nullable().optional(),
  entrySourceType: z.string().nullable().optional(),
  entryDate: z.string().nullable().optional(),
  result: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  stepDate: z.string().nullable().optional(),
  deadline: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  place: z.string().nullable().optional(),
});

export const UpdateEntryManagementInput = z.object({
  companyID: z.number(),
  companyName: z.string(),
  overallStatus: ManagementOverAllStatus.optional(),
  hasCompletedEntry: z.boolean().optional(),
  steps: z.array(UpdateEntryManagementStepInput).optional(),
});

export const UpdateEntryManagementOutput = z.object({
  id: z.number(),
  companyID: z.number(),
  companyName: z.string(),
  overallStatus: z.string(),
  steps: z.array(CreatedStep),
  googleSyncResult: GoogleSyncResult.optional(),
});

// POST /3.0/service/users/me/entry-managements/steps - 選考予定自動生成API
// アクションによる選考予定自動生成用（エントリー完了時、予定追加時）
// companyIDを渡すことで既存選考管理に選考予定追加、または管理データ作成→選考予定作成
// companyIDまたはcompanyNameのどちらかがあれば選考予定作成可能
export const CreateStepInput = z.object({
  companyID: z.number().optional(),
  companyName: z.string(),
  stepName: z.string(),
  stepOrder: z.number(),
  entryTitle: z.string().optional(),
  entrySourceUrl: z.string().optional(),
  entrySourceType: z.string().optional(),
  entryDate: z.string().optional(), // YYYY-MM-DD形式
  scheduleID: z.number().optional(),
  result: z.string().optional(),
  notes: z.string().optional(),
  deadline: z.string().optional(), // YYYY-MM-DD形式
  startDate: z.string().optional(), // YYYY-MM-DD形式
  endDate: z.string().optional(), // YYYY-MM-DD形式
  place: z.string().optional(),
});

export const CreateStepOutput = z.object({
  id: z.number(),
  userEntryManagementID: z.number(),
  stepName: z.string(),
  stepOrder: z.number(),
  created: z.string(),
  modified: z.string(),
  googleSyncResult: GoogleSyncResult.optional(),
});

// DELETE /3.0/service/users/me/entry-managements/{id} - 選考管理削除API
// Google連携時は200（同期結果あり）、未連携時は204 No Content
export const DeleteEntryManagementOutput = z.object({
  googleSyncResult: GoogleSyncResult.optional(),
});

// DELETE /3.0/service/users/me/entry-managements/steps/{stepId} - ステップ削除API
export const DeleteStepOutput = z.object({
  googleSyncResult: GoogleSyncResult.optional(),
});

// POST /3.0/service/users/me/entry-managements/steps/text-analyze - 選考管理選考予定AI解析作成API
// POST /3.0/service/users/me/entry-managements/steps/image-analyze - 選考管理選考予定画像AI解析作成API
// 共通スキーマ: テキスト解析と画像解析のレスポンスは同じ構造
export const AnalyzeStepOutput = z.object({
  id: z.number(),
  userEntryManagementID: z.number(),
  companyID: z.number().nullable(),
  companyName: z.string(),
  stepName: z.string(),
  stepOrder: z.number(),
  entryTitle: z.string(),
  sourceType: z.string(),
  entrySourceUrl: z.string(),
  entrySourceType: z.string(),
  entryDate: z.string(),
  result: z.string(),
  notes: z.string(),
  deadline: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  place: z.string(),
  created: z.string(),
  modified: z.string(),
});

// テキスト解析と画像解析は同じスキーマを使用
export const TextAnalyzeStepOutput = AnalyzeStepOutput;
export const ImageAnalyzeStepOutput = AnalyzeStepOutput;

// AI解析APIの新しい候補スキーマ（DB保存前のプレビュー用）
export const StepCandidate = z.object({
  stepName: z.string(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  deadline: z.string().nullable().optional(),
  place: z.string().optional(),
  notes: z.string().optional(),
});

export const AnalyzeStepCandidatesResponse = z.object({
  companyID: z.number().nullable().optional(),
  companyName: z.string().nullable().optional(),
  userEntryManagementID: z.number().optional(),
  candidates: z.array(StepCandidate),
});

// GET /3.0/entry_managements/reminders - リマインダー件数取得API
export const RemindersResponse = z.object({
  deadlineWithinThreeDays: z.number(),
  deadlineWithinDay: z.number(),
  startDateWithinWeek: z.number(),
  startDateWithinDay: z.number(),
});
